import { useContext } from 'react';
import { useTracking } from 'react-tracking';
import { BreadcrumbListItem } from './BreadcrumbListItem';
import DocumentSchema from '@components/DocumentSchema';
import type { BreadcrumbList, ListItem, URL } from 'schema-dts';
import { SWS_HOST } from '@/constants';
import { slugify } from '@utilities/slugify';
import { Context as OriginContext } from '@/context/Origin';
import { actionToPath } from 'redux-first-router';
import queryString from 'query-string';
import routesGenerator from '@/router/routesGenerator';

export interface BreadcrumbItem {
  name: string;
  canonicalUrl?: string;
  dispatch?: ReducerAction;
  documentSchemaOnly?: boolean;
}

type Props = {
  list: BreadcrumbItem[] | readonly BreadcrumbItem[];
};

const routesMap = routesGenerator();

export const Breadcrumb = ({ list }: Props) => {
  const origin = useContext(OriginContext);
  const { Track } = useTracking({
    type: 'track',
    subject: 'breadcrumb',
    breadcrumb_length: list.length,
  });

  const schemaItemListElement = list
    .filter(
      ({ name, dispatch, canonicalUrl }) => !!name && (dispatch || canonicalUrl)
    )
    .map(({ name, canonicalUrl, dispatch }, i) => {
      const listItem = {
        '@type': 'ListItem',
        position: i + 1,
        name: name,
      };
      if (canonicalUrl) {
        /** Use canonicalUrl */
        listItem['item'] = `${SWS_HOST}${canonicalUrl}` as URL;
      } else if (dispatch) {
        try {
          /** Derive item from route action */
          const path = actionToPath(
            {
              ...dispatch,
              payload: dispatch.payload ?? {},
            },
            routesMap,
            queryString
          );
          listItem['item'] = `${origin}${path}`;
        } catch (e) {
          // 🤷‍♂️ boo-hoo meta data is broken it's the end of the world
        }
      }
      return listItem;
    }) as ListItem[];

  const visibleListItems = list.filter((value) => !value.documentSchemaOnly);
  return (
    <Track>
      {Boolean(schemaItemListElement.length) && (
        <DocumentSchema<BreadcrumbList>
          item={{
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: schemaItemListElement,
          }}
          id="breadcrumb-list"
        />
      )}
      {Boolean(visibleListItems.length) && (
        <ul className="flex flex-wrap gap-x1">
          {visibleListItems.map((value, index, self) => {
            const isLast = self.length - 1 === index;
            return (
              <BreadcrumbListItem
                key={`Breadcrumb-${slugify(value.name)}`}
                to={value.dispatch}
                title={value.name}
                index={index}
                isLast={isLast}
              />
            );
          })}
        </ul>
      )}
    </Track>
  );
};
